import React, { useState, useRef, useEffect, forwardRef, useMemo } from 'react'
import { AutocompleteProps, AutocompleteOption } from './Autocomplete.types'
import {
  AutocompleteWrapper,
  AutocompleteDropdown,
  AutocompleteOptionsList,
  AutocompleteOptionItem,
  AutocompleteCategoryHeader,
  AutocompleteNoOptions,
  AutocompleteLoading,
} from './Autocomplete.styles'
import { Input } from '../Input'
import { MultiInput } from '../MultiInput'
import { Icon } from '../Icon'

export const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>(
  (
    {
      options = [],
      value,
      values,
      multiple = false,
      onChange,
      onMultipleChange,
      onInputChange,
      placeholder = 'Search...',
      allowCustomValue = false,
      clearOnSelect = false,
      closeOnSelect = true,
      openOnFocus = true,
      minSearchLength = 0,
      maxOptions = 50,
      dropdownLayout = 'overlay',
      noOptionsText = 'No options found',
      loadingText = 'Loading...',
      loading = false,
      filterOptions,
      renderOption,
      groupByCategory = false,
      maxSelectedDisplay = 3,
      selectedOverflowText = (count: number) => `+${count} more`,
      size = 'medium',
      error = false,
      fullWidth = false,
      disabled = false,
      className,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const [selectedOption, setSelectedOption] =
      useState<AutocompleteOption | null>(null)
    const [selectedOptions, setSelectedOptions] = useState<
      AutocompleteOption[]
    >([])

    const inputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const optionsRefs = useRef<(HTMLDivElement | null)[]>([])

    // Find selected options based on values (multiple mode) or value (single mode)
    useEffect(() => {
      if (multiple && values) {
        const selected = options.filter((opt) =>
          values.includes(String(opt.value))
        )
        setSelectedOptions(selected)
      } else if (!multiple && value) {
        const option = options.find((opt) => opt.value === value)
        if (option) {
          setSelectedOption(option)
          setSearchValue(option.label)
        }
      } else {
        if (multiple) {
          setSelectedOptions([])
        } else {
          setSelectedOption(null)
          setSearchValue('')
        }
      }
    }, [multiple, value, values, options])

    // Default filter function
    const defaultFilterOptions = (
      opts: AutocompleteOption[],
      search: string
    ) => {
      if (search.length < minSearchLength) return []

      let filteredOpts = opts.filter(
        (option) =>
          option.label.toLowerCase().includes(search.toLowerCase()) ||
          String(option.value).toLowerCase().includes(search.toLowerCase())
      )

      // In multiple mode, exclude already selected options
      if (multiple) {
        const selectedValues = selectedOptions.map((opt) => opt.value)
        filteredOpts = filteredOpts.filter(
          (option) => !selectedValues.includes(option.value)
        )
      }

      return filteredOpts
    }

    // Filter and limit options
    const filteredOptions = useMemo(() => {
      const filterFn = filterOptions || defaultFilterOptions
      const filtered = filterFn(options, searchValue)
      return filtered.slice(0, maxOptions)
    }, [
      options,
      searchValue,
      filterOptions,
      maxOptions,
      minSearchLength,
      multiple,
      selectedOptions,
    ])

    // Group options by category if enabled
    const groupedOptions = useMemo(() => {
      if (!groupByCategory) return { '': filteredOptions }

      return filteredOptions.reduce(
        (groups, option) => {
          const category = option.category || ''
          if (!groups[category]) groups[category] = []
          groups[category].push(option)
          return groups
        },
        {} as Record<string, AutocompleteOption[]>
      )
    }, [filteredOptions, groupByCategory])

    // Flatten grouped options for keyboard navigation
    const flatOptions = useMemo(() => {
      return Object.values(groupedOptions).flat()
    }, [groupedOptions])

    // Handle outside click
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current &&
          !inputRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
          document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isOpen])

    // Scroll highlighted option into view
    useEffect(() => {
      if (highlightedIndex >= 0 && optionsRefs.current[highlightedIndex]) {
        optionsRefs.current[highlightedIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      }
    }, [highlightedIndex])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setSearchValue(newValue)
      setIsOpen(true)
      setHighlightedIndex(-1)

      onInputChange?.(newValue)

      // Clear selection if input doesn't match any option (single mode only)
      if (!multiple && !allowCustomValue) {
        const matchingOption = options.find((opt) => opt.label === newValue)
        if (!matchingOption && selectedOption) {
          setSelectedOption(null)
          onChange?.(null)
        }
      }
    }

    const handleInputFocus = () => {
      if (openOnFocus && !disabled) {
        setIsOpen(true)
      }
    }

    const handleOptionSelect = (option: AutocompleteOption) => {
      if (option.disabled) return

      if (multiple) {
        const newSelected = [...selectedOptions, option]
        setSelectedOptions(newSelected)
        onMultipleChange?.(newSelected)

        // Always clear search in multiple mode
        setSearchValue('')

        if (closeOnSelect) {
          setIsOpen(false)
        }
      } else {
        setSelectedOption(option)
        onChange?.(option)

        if (clearOnSelect) {
          setSearchValue('')
        } else {
          setSearchValue(option.label)
        }

        if (closeOnSelect) {
          setIsOpen(false)
          inputRef.current?.blur()
        }
      }

      setHighlightedIndex(-1)
    }

    const handleTagRemove = (optionToRemove: AutocompleteOption) => {
      if (!multiple) return

      const newSelected = selectedOptions.filter(
        (opt) => opt.value !== optionToRemove.value
      )
      setSelectedOptions(newSelected)
      onMultipleChange?.(newSelected)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
          e.preventDefault()
          setIsOpen(true)
        }

        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex((prev) => {
            const nextIndex = prev < flatOptions.length - 1 ? prev + 1 : prev
            // Skip disabled options
            const nextOption = flatOptions[nextIndex]
            if (nextOption?.disabled && nextIndex < flatOptions.length - 1) {
              return nextIndex + 1
            }
            return nextIndex
          })
          break

        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : prev
            // Skip disabled options
            const nextOption = flatOptions[nextIndex]

            if (nextOption?.disabled && nextIndex > 0) {
              return nextIndex - 1
            }
            return nextIndex
          })
          break

        case 'Enter':
          e.preventDefault()
          if (highlightedIndex >= 0 && flatOptions[highlightedIndex]) {
            handleOptionSelect(flatOptions[highlightedIndex])
          } else if (allowCustomValue && searchValue.trim()) {
            const customOption: AutocompleteOption = {
              value: searchValue.trim(),
              label: searchValue.trim(),
            }
            handleOptionSelect(customOption)
          }
          break

        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          inputRef.current?.blur()
          break

        case 'Tab':
          setIsOpen(false)
          break

        case 'Backspace':
          // In multiple mode, remove last selected item if search is empty
          if (multiple && searchValue === '' && selectedOptions.length > 0) {
            e.preventDefault()
            const lastOption = selectedOptions[selectedOptions.length - 1]
            handleTagRemove(lastOption)
          }
          break
      }
    }

    const showDropdown =
      isOpen &&
      (loading ||
        flatOptions.length > 0 ||
        (searchValue.length >= minSearchLength && noOptionsText))

    const rightIcon = isOpen ? (
      <Icon name="arrow-up" size="small" />
    ) : (
      <Icon name="arrow-down" size="small" />
    )

    // Prepare display data for multiple mode
    const multiInputTags = selectedOptions.map((option) => ({
      id: String(option.value),
      label: option.label,
      removable: true,
    }))

    return (
      <AutocompleteWrapper
        $fullWidth={fullWidth}
        $isOpen={isOpen}
        className={className}
      >
        {multiple ? (
          <MultiInput
            ref={(node) => {
              inputRef.current = node
              if (ref) {
                if (typeof ref === 'function') {
                  ref(node)
                } else {
                  ref.current = node
                }
              }
            }}
            tags={multiInputTags}
            onTagRemove={(tag) => {
              const optionToRemove = selectedOptions.find(
                (opt) => opt.value === tag.id
              )
              if (optionToRemove) {
                handleTagRemove(optionToRemove)
              }
            }}
            maxVisibleTags={maxSelectedDisplay}
            overflowText={selectedOverflowText}
            value={searchValue}
            onChange={(newValue) => {
              setSearchValue(newValue)
              setIsOpen(true)
              setHighlightedIndex(-1)
              onInputChange?.(newValue)
            }}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            size={size}
            error={error}
            fullWidth={fullWidth}
            disabled={disabled}
            rightIcon={rightIcon}
            autoComplete="off"
            {...Object.fromEntries(
              Object.entries(props).filter(([key]) => key !== 'children')
            )}
          />
        ) : (
          <Input
            ref={(node) => {
              inputRef.current = node
              if (ref) {
                if (typeof ref === 'function') {
                  ref(node)
                } else {
                  ref.current = node
                }
              }
            }}
            value={searchValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            size={size}
            error={error}
            fullWidth={fullWidth}
            disabled={disabled}
            rightIcon={rightIcon}
            autoComplete="off"
            {...props}
          />
        )}

        {showDropdown && (
          <AutocompleteDropdown
            ref={dropdownRef}
            $isOpen={isOpen}
            $fullWidth={fullWidth}
            $layout={dropdownLayout}
          >
            <AutocompleteOptionsList>
              {loading ? (
                <AutocompleteLoading>
                  <Icon name="loading" size="small" />
                  {loadingText}
                </AutocompleteLoading>
              ) : flatOptions.length === 0 ? (
                <AutocompleteNoOptions>{noOptionsText}</AutocompleteNoOptions>
              ) : (
                Object.entries(groupedOptions).map(
                  ([category, categoryOptions], categoryIndex) => (
                    <React.Fragment key={category}>
                      {groupByCategory && category && (
                        <AutocompleteCategoryHeader
                          $isFirst={categoryIndex === 0}
                        >
                          {category}
                        </AutocompleteCategoryHeader>
                      )}
                      {categoryOptions.map((option) => {
                        const flatIndex = flatOptions.findIndex(
                          (opt) => opt === option
                        )
                        const isHighlighted = flatIndex === highlightedIndex
                        const isSelected = multiple
                          ? selectedOptions.some(
                              (selected) => selected.value === option.value
                            )
                          : selectedOption?.value === option.value

                        return (
                          <AutocompleteOptionItem
                            key={`${category}-${option.value}`}
                            ref={(el) => {
                              optionsRefs.current[flatIndex] = el
                            }}
                            $isHighlighted={isHighlighted}
                            $isSelected={isSelected}
                            $disabled={option.disabled || false}
                            onClick={() => handleOptionSelect(option)}
                            onMouseEnter={() => setHighlightedIndex(flatIndex)}
                            role="option"
                            aria-selected={isSelected}
                            aria-disabled={option.disabled}
                            title={option.title}
                          >
                            <>
                              {renderOption
                                ? renderOption(option, isHighlighted)
                                : option.label}
                            </>
                          </AutocompleteOptionItem>
                        )
                      })}
                    </React.Fragment>
                  )
                )
              )}
            </AutocompleteOptionsList>
          </AutocompleteDropdown>
        )}
      </AutocompleteWrapper>
    )
  }
)

Autocomplete.displayName = 'Autocomplete'
